import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { CsvIcon } from '@/icons/csv-icon';
import { PlusIcon } from '@/icons/plus-icon';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { StaticDataTypeLabel } from '@/services/staticDataTypeLabel/staticDataTypeLabel.types';
import { useParams, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import { getStaticDataLabelExportKeysByColumn, keepVisibleDataLabelExportColumns } from '../exportData';
import { useDeleteStaticDataTypeLabel } from '../hooks/useStaticDataTypeLabelMutations';
import { ManageStaticDataTypeLabelContainer } from '../ManageStaticDataTypeLabelContainer';
import { getColumns } from './Columns';

export const StaticDataTypeLabelTable = ({
  data,
  isLoading = false,
  pagination,
}: {
  data: StaticDataTypeLabel[];
  isLoading: boolean;
  pagination: Pagination;
}) => {
  const { staticDataTypeName } = useParams({ from: '/(app)/logs-management/static-data-type/$staticDataTypeName/' });

  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { staticDataTypeId } = useSearch({ from: '/(app)/logs-management/static-data-type/$staticDataTypeName/' });

  const { mutateAsync } = useDeleteStaticDataTypeLabel();
  const columns = React.useMemo(
    () => getColumns(t, onOpen, openConfirmation, mutateAsync, staticDataTypeId),
    [t, onOpen, openConfirmation, mutateAsync, staticDataTypeId],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const finalData = data?.map((val: StaticDataTypeLabel, index: number) => {
    return {
      SN: index + 1,
      [t('logsManagement.staticDataTypeLabel.page.table.staticDataLabel')]: val.staticDataLabel,
      [t('logsManagement.staticDataTypeLabel.page.table.staticDataDescription')]: val.staticDataDescription,
    };
  });
  const exportKeysByColumn = React.useMemo<Record<string, readonly string[]>>(
    () => getStaticDataLabelExportKeysByColumn(t),
    [t],
  );
  const onExportCsv = () => {
    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = keepVisibleDataLabelExportColumns(finalData, visibleColumnIds, exportKeysByColumn);
    exportCsvSingleFilePapa(visibleData);
  };
  const canAddNew = useCan({ allOf: [PERMS.static.dataLabel.create] });
  const onAddNew = () => {
    onOpen({
      title: 'logsManagement.staticDataTypeLabel.manageStaticDataTypeLabel.title',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'logsManagement.staticDataTypeLabel.manageStaticDataTypeLabel.title',
      component: <ManageStaticDataTypeLabelContainer staticDataTypeId={staticDataTypeId} />,
    });
  };
  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'sno', desc: true }],
    },
    meta: {
      title: staticDataTypeName,
      actions: [
        {
          key: 'exportCsv',
          icon: <CsvIcon />,
          onClick: onExportCsv,
        },
        ...(canAddNew
          ? [
              {
                key: 'onAddNew',
                icon: <PlusIcon />,
                onClick: onAddNew,
              },
            ]
          : []),
      ],
      loading: isLoading,
      totalPage: pagination.totalItems,
      hideSearch: true,
    },
    getRowId: (originalRow) => String(originalRow.sno),
    shallow: false,
    clearOnDefault: true,
  });
  return (
    <div>
      <DataTable table={table} className="rounded-lg">
        <DataTableToolbar />
      </DataTable>
    </div>
  );
};
